"use client"

import createGlobe from "cobe"
import { useCallback, useEffect, useRef, useState } from "react"
import { cn } from "../../lib/utils"

const GLOBE_CONFIG = {
  width: 800,
  height: 800,
  onRender: () => {},
  devicePixelRatio: 2,
  phi: 0,
  theta: 0.3,
  dark: 0,
  diffuse: 0.4,
  mapSamples: 16000,
  mapBrightness: 1.2,
  baseColor: [1, 1, 1],
  markerColor: [0 / 255, 95 / 255, 115 / 255], // Ocean blue color
  glowColor: [0.9, 0.95, 1],
  markers: [
    // Portugal
    { location: [38.7223, -9.1393], size: 0.08 }, // Lisboa
    { location: [41.1579, -8.6291], size: 0.05 }, // Porto
    // Popular travel destinations
    { location: [25.2048, 55.2708], size: 0.07 }, // Dubai
    { location: [4.1755, 73.5093], size: 0.06 }, // Maldivas
    { location: [36.4618, 25.3753], size: 0.05 }, // Santorini
    { location: [-8.4095, 115.1889], size: 0.05 }, // Bali
    { location: [21.4225, -158.0], size: 0.05 }, // Hawaii
    { location: [-33.8688, 151.2093], size: 0.06 }, // Sydney
    { location: [48.8566, 2.3522], size: 0.06 }, // Paris
    { location: [41.9028, 12.4964], size: 0.05 }, // Roma
    { location: [35.6762, 139.6503], size: 0.06 }, // Tokyo
    { location: [40.7128, -74.006], size: 0.07 }, // New York
    { location: [-22.9068, -43.1729], size: 0.06 }, // Rio de Janeiro
    { location: [1.3521, 103.8198], size: 0.05 }, // Singapore
  ],
}

export function Globe({
  className,
  config = GLOBE_CONFIG,
}) {
  let phi = 0
  let width = 0
  const canvasRef = useRef(null)
  const pointerInteracting = useRef(null)
  const pointerInteractionMovement = useRef(0)
  const [r, setR] = useState(0)

  const updatePointerInteraction = (value) => {
    pointerInteracting.current = value
    if (canvasRef.current) {
      canvasRef.current.style.cursor = value ? "grabbing" : "grab"
    }
  }

  const updateMovement = (clientX) => {
    if (pointerInteracting.current !== null) {
      const delta = clientX - pointerInteracting.current
      pointerInteractionMovement.current = delta
      setR(delta / 200)
    }
  }

  const onRender = useCallback(
    (state) => {
      if (!pointerInteracting.current) phi += 0.005
      state.phi = phi + r
      state.width = width * 2
      state.height = width * 2
    },
    [r],
  )

  const onResize = () => {
    if (canvasRef.current) {
      width = canvasRef.current.offsetWidth
    }
  }

  useEffect(() => {
    window.addEventListener("resize", onResize)
    onResize()

    const globe = createGlobe(canvasRef.current, {
      ...config,
      width: width * 2,
      height: width * 2,
      onRender,
    })

    setTimeout(() => {
      if (canvasRef.current) {
        canvasRef.current.style.opacity = "1"
      }
    })
    return () => {
      globe.destroy()
      window.removeEventListener("resize", onResize)
    }
  }, [config, onRender])

  return (
    <div
      className={cn(
        "absolute inset-0 mx-auto aspect-[1/1] w-full max-w-[600px]",
        className,
      )}
    >
      <canvas
        className={cn(
          "size-full opacity-0 transition-opacity duration-500 [contain:layout_paint_size]",
        )}
        ref={canvasRef}
        onPointerDown={(e) =>
          updatePointerInteraction(
            e.clientX - pointerInteractionMovement.current,
          )
        }
        onPointerUp={() => updatePointerInteraction(null)}
        onPointerOut={() => updatePointerInteraction(null)}
        onMouseMove={(e) => updateMovement(e.clientX)}
        onTouchMove={(e) =>
          e.touches[0] && updateMovement(e.touches[0].clientX)
        }
      />
    </div>
  )
}

export default Globe
